import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import {
  Brain, MessageSquare, ThumbsUp, ThumbsDown, CheckCircle, XCircle,
  Loader, BarChart3, TrendingUp, Clock, Filter, Eye, Edit3,
  AlertTriangle, Star, RefreshCw, Download, ChevronRight
} from 'lucide-react';

// Feedback Stats Card
const StatsCard = ({ icon: Icon, label, value, subtext, color = 'emerald' }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg bg-${color}-100`}>
        <Icon size={20} className={`text-${color}-600`} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {subtext && <p className="text-xs text-slate-400">{subtext}</p>}
      </div>
    </div>
  </div>
);

// Feedback Type Badge
const FeedbackBadge = ({ type }) => {
  const colors = {
    helpful: 'bg-emerald-100 text-emerald-700',
    not_helpful: 'bg-red-100 text-red-700',
    partially_helpful: 'bg-amber-100 text-amber-700',
    correction: 'bg-blue-100 text-blue-700',
    enhancement: 'bg-purple-100 text-purple-700'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[type] || 'bg-slate-100 text-slate-600'}`}>
      {type?.replace('_', ' ')}
    </span>
  );
};

// Quality Badge
const QualityBadge = ({ quality }) => {
  const colors = {
    high: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    medium: 'bg-blue-100 text-blue-700 border-blue-200',
    low: 'bg-amber-100 text-amber-700 border-amber-200',
    rejected: 'bg-red-100 text-red-700 border-red-200'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${colors[quality] || colors.medium}`}>
      {quality?.toUpperCase()}
    </span>
  );
};

// Training Example Card
const TrainingExampleCard = ({ example, onReview }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono bg-slate-100 text-slate-600 px-2 py-1 rounded">
            {example.agent_name}
          </span>
          <QualityBadge quality={example.quality} />
          {example.reviewed && (
            <CheckCircle size={14} className="text-emerald-500" />
          )}
        </div>
        <span className="text-xs text-slate-400">
          {new Date(example.created_at).toLocaleDateString()}
        </span>
      </div>
      
      <div className="space-y-3">
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">Prompt</p>
          <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded line-clamp-2">
            {example.prompt}
          </p>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-medium text-red-500 mb-1">Original Response</p>
            <p className="text-xs text-slate-600 bg-red-50 p-2 rounded line-clamp-3">
              {example.original_response}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-emerald-500 mb-1">Improved Response</p>
            <p className="text-xs text-slate-600 bg-emerald-50 p-2 rounded line-clamp-3">
              {example.improved_response}
            </p>
          </div>
        </div>
      </div>
      
      {!example.reviewed && (
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onReview(example.example_id, 'high')}
            className="flex-1 py-2 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-700 transition-colors"
          >
            Approve (High)
          </button>
          <button
            onClick={() => onReview(example.example_id, 'medium')}
            className="flex-1 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
          >
            Approve (Medium)
          </button>
          <button
            onClick={() => onReview(example.example_id, 'rejected')}
            className="flex-1 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
};

// Agent Performance Chart (simplified)
const AgentPerformanceChart = ({ data }) => {
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <BarChart3 size={32} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">No agent performance data yet</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {Object.entries(data).map(([agent, stats]) => (
        <div key={agent} className="flex items-center gap-3">
          <span className="w-32 text-sm font-medium text-slate-700 truncate">{agent.replace('_', ' ')}</span>
          <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
            <div 
              className="h-full bg-emerald-500 rounded-full"
              style={{ width: `${(stats.satisfaction_rate || 0) * 100}%` }}
            />
          </div>
          <span className="text-sm font-bold text-slate-700 w-16 text-right">
            {((stats.satisfaction_rate || 0) * 100).toFixed(0)}%
          </span>
          <span className="text-xs text-slate-400 w-16">({stats.total} reviews)</span>
        </div>
      ))}
    </div>
  );
};

// Main Fine-tuning Dashboard
const FineTuningDashboard = () => {
  const [stats, setStats] = useState(null);
  const [examples, setExamples] = useState([]);
  const [unreviewedExamples, setUnreviewedExamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [agentFilter, setAgentFilter] = useState('all');

  const fetchData = useCallback(async () => {
    const [statsData, examplesData, unreviewedData] = await Promise.all([
      apiFetch(`${API}/fine-tuning/feedback-stats`),
      apiFetch(`${API}/fine-tuning/training-examples?limit=50`),
      apiFetch(`${API}/fine-tuning/unreviewed?limit=20`)
    ]);

    if (statsData?._error || examplesData?._error || unreviewedData?._error) {
      console.error('Failed to fetch:', statsData?.message || examplesData?.message || unreviewedData?.message);
      setLoading(false);
      return;
    }

    setStats(statsData);
    setExamples(examplesData.examples || []);
    setUnreviewedExamples(unreviewedData.examples || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleReview = async (exampleId, quality) => {
    const data = await apiFetch(`${API}/fine-tuning/review-example`, {
      method: 'POST',
      body: {
        example_id: exampleId,
        quality: quality
      }
    });
    if (data?._error) {
      console.error('Failed to review:', data.message);
      return;
    }
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }

  const agents = stats?.by_agent ? Object.keys(stats.by_agent) : [];

  return (
    <div className="space-y-6" data-testid="fine-tuning-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-1">Fine-Tuning</h1>
            <p className="text-violet-200 text-sm">
              Collect feedback, review training examples, and track model performance
            </p>
          </div>
          <button
            onClick={fetchData}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
          >
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          icon={MessageSquare}
          label="Total Feedback"
          value={stats?.total_feedback || 0}
          subtext={`Last ${stats?.period_days || 30} days`}
          color="blue"
        />
        <StatsCard
          icon={ThumbsUp}
          label="Satisfaction Rate"
          value={`${((stats?.satisfaction_rate || 0) * 100).toFixed(0)}%`}
          color="emerald"
        />
        <StatsCard
          icon={Star}
          label="Avg Rating"
          value={stats?.average_rating?.toFixed(1) || 'N/A'}
          subtext={`${stats?.rated_interactions || 0} rated`}
          color="amber"
        />
        <StatsCard
          icon={Brain}
          label="Training Examples"
          value={stats?.training_examples_generated || 0}
          color="purple"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'review', label: `Review Queue (${unreviewedExamples.length})`, icon: Eye },
          { id: 'examples', label: 'All Examples', icon: Brain }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors flex items-center gap-2 ${
              activeTab === tab.id 
                ? 'bg-violet-100 text-violet-700 border-b-2 border-violet-600' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-bold text-slate-900 mb-4">Agent Satisfaction Rates</h3>
            <AgentPerformanceChart data={stats?.by_agent} />
          </div>
          
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-bold text-slate-900 mb-4">Feedback Distribution</h3>
            <div className="space-y-3">
              {Object.entries(stats?.feedback_by_type || {}).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <FeedbackBadge type={type} />
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-violet-500 rounded-full"
                        style={{ width: `${(count / (stats?.total_feedback || 1)) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-slate-700 w-10 text-right">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Review Queue Tab */}
      {activeTab === 'review' && (
        <div className="space-y-4">
          {unreviewedExamples.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <CheckCircle size={48} className="mx-auto text-emerald-500 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">All Caught Up!</h3>
              <p className="text-sm text-slate-500">No training examples pending review</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {unreviewedExamples.map(example => (
                <TrainingExampleCard
                  key={example.example_id}
                  example={example}
                  onReview={handleReview}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Examples Tab */}
      {activeTab === 'examples' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <select
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm"
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent} value={agent}>{agent.replace('_', ' ')}</option>
              ))}
            </select>
            <span className="text-sm text-slate-500">
              {examples.length} examples total
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {examples
              .filter(ex => agentFilter === 'all' || ex.agent_name === agentFilter)
              .map(example => (
                <TrainingExampleCard
                  key={example.example_id}
                  example={example}
                  onReview={handleReview}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FineTuningDashboard;
